import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Search, Edit, Eye, Trash2 } from 'lucide-react';

type Fornecedor = {
  id: string;
  codigo_fornecedor: string;
  cnpj: string;
  razao_social: string;
  apelido: string | null;
  inscricao_estadual: string | null;
  inscricao_municipal: string | null;
  email_nfe: string | null;
  fora_municipio: boolean | null;
  ibge_municipio: string | null;
  limite_credito: number | null;
  matricula_cei_cno: string | null;
  agencia: string | null;
  conta: string | null;
  pix: string | null;
  banco: string | null;
  cnpj_bancario: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
};

export default function Fornecedores() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; fornecedor: Fornecedor | null }>({
    open: false,
    fornecedor: null,
  });
  const loadingRef = useRef(false);

  const canRead = useMemo(() => hasPermission('clientes', 'fornecedores', 'read'), [hasPermission]);
  const canWrite = useMemo(() => hasPermission('clientes', 'fornecedores', 'write'), [hasPermission]);
  const canAdmin = useMemo(() => hasPermission('clientes', 'fornecedores', 'admin'), [hasPermission]);

  const loadFornecedores = async () => {
    if (loadingRef.current || !canRead) return;
    
    loadingRef.current = true;
    try {
      const { data, error } = await supabase
        .from('fornecedores')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFornecedores((data || []).map(fornecedor => ({ ...fornecedor, ativo: fornecedor.status === 'ativo' })));
    } catch (error: any) {
      console.error('Erro ao carregar fornecedores:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar fornecedores: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (canRead) {
      loadFornecedores();
    } else {
      setLoading(false);
    }
  }, [canRead]);

  const filteredFornecedores = useMemo(() => {
    if (!searchTerm) return fornecedores;
    
    const term = searchTerm.toLowerCase();
    return fornecedores.filter(fornecedor =>
      fornecedor.razao_social.toLowerCase().includes(term) ||
      fornecedor.cnpj.toLowerCase().includes(term) ||
      fornecedor.codigo_fornecedor.toLowerCase().includes(term) ||
      (fornecedor.apelido && fornecedor.apelido.toLowerCase().includes(term))
    );
  }, [fornecedores, searchTerm]);

  const handleDelete = async (fornecedor: Fornecedor) => {
    try {
      const { error } = await supabase
        .from('fornecedores')
        .delete()
        .eq('id', fornecedor.id);

      if (error) throw error;

      setFornecedores(prev => prev.filter(f => f.id !== fornecedor.id));
      toast({
        title: "Sucesso",
        description: "Fornecedor excluído com sucesso!",
      });
    } catch (error: any) {
      console.error('Erro ao excluir fornecedor:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir fornecedor: " + error.message,
        variant: "destructive",
      });
    }
    setDeleteDialog({ open: false, fornecedor: null });
  };

  if (!canRead) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Você não tem permissão para visualizar fornecedores.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fornecedores</h1>
          <p className="text-muted-foreground">
            Gerencie os fornecedores da empresa
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => navigate('/intranet/fornecedores/novo')}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Fornecedor
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Fornecedores</CardTitle>
          <div className="flex items-center space-x-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar fornecedores..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredFornecedores.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {searchTerm ? 'Nenhum fornecedor encontrado.' : 'Nenhum fornecedor cadastrado.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Razão Social</TableHead>
                  <TableHead>Apelido</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFornecedores.map((fornecedor) => (
                  <TableRow key={fornecedor.id}>
                    <TableCell className="font-medium">
                      {fornecedor.codigo_fornecedor}
                    </TableCell>
                    <TableCell>{fornecedor.cnpj}</TableCell>
                    <TableCell>{fornecedor.razao_social}</TableCell>
                    <TableCell>{fornecedor.apelido || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={fornecedor.ativo ? "default" : "secondary"}>
                        {fornecedor.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/intranet/fornecedores/${fornecedor.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/intranet/fornecedores/${fornecedor.id}/edit`)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {canAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteDialog({ open: true, fornecedor })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog 
        open={deleteDialog.open} 
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, fornecedor: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o fornecedor "{deleteDialog.fornecedor?.razao_social}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, fornecedor: null })}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteDialog.fornecedor && handleDelete(deleteDialog.fornecedor)}
            >
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}